import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  Chip,
  Alert,
  AlertTitle,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import MT5Connection from '../components/MT5Connection/MT5Connection.component';
import MT5TradingPanel from '../components/MT5TradingPanel/MT5TradingPanel.component';
import MT5LiveChartPanel from '../components/MT5LiveChartPanel';

const MT5Trading: React.FC = () => {
  const theme = useTheme();
  const [isConnected, setIsConnected] = useState(false);
  const [showChartPanel, setShowChartPanel] = useState(() => {
    const saved = localStorage.getItem('mt5ShowLiveChartPanel');
    return saved === 'true';
  });

  const handleConnectionChange = (connected: boolean) => {
    setIsConnected(connected);
  };

  // Persist showChartPanel state
  useEffect(() => {
    localStorage.setItem('mt5ShowLiveChartPanel', showChartPanel ? 'true' : 'false');
  }, [showChartPanel]);

  return (
    <Box 
      sx={{ 
        maxWidth: '960px',
        width: '100%',
        mx: 'auto',
        px: { xs: 1, sm: 3 },
        pt: { xs: 2, sm: 3 },
        pb: { xs: 7, sm: 3 },
        minHeight: '100vh',
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h5" sx={{ fontSize: '1.5rem', mb: 1 }}>MT5 Trading Platform</Typography>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: 1
        }}>
          <Typography 
            variant="subtitle1" 
            color="text.secondary" 
            sx={{ 
              fontSize: '0.875rem',
              mb: { xs: 1, sm: 0 }
            }}
          >
            Professional trading with real-time market data and advanced order execution
          </Typography>
          <Box sx={{
            display: 'flex',
            gap: 0.5,
            flexWrap: 'wrap',
            justifyContent: 'flex-end'
          }}>
            <Chip
              label={isConnected ? 'Connected' : 'Disconnected'}
              color={isConnected ? 'success' : 'error'}
              size="small"
              icon={isConnected ? <TrendingUpIcon /> : <SecurityIcon />}
              sx={{
                fontSize: '0.75rem',
                height: '24px',
              }}
            />
            <Button
              variant="outlined"
              size="small"
              sx={{ ml: 2, fontSize: 13, borderRadius: 2, borderColor: '#007bff', color: '#007bff' }}
              onClick={() => setShowChartPanel(v => !v)}
              disabled={!isConnected}
            >
              {showChartPanel ? 'Hide' : 'Show'} Live Chart
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Main Content Grid */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1fr 3fr' },
        gap: 3
      }}>
        {/* Connection Panel - Left Sidebar */}
        <Box sx={{ order: { xs: 2, lg: 1 } }}>
          <Box sx={{ position: 'sticky', top: 16 }}>
            <MT5Connection onConnectionChange={handleConnectionChange} />
          </Box>
        </Box>

        {/* Main Trading Area */}
        <Box sx={{ order: { xs: 1, lg: 2 } }}>
            <MT5TradingPanel isConnected={isConnected} />
        </Box>
      </Box>

      {/* Draggable Live Chart Panel */}
      {showChartPanel && (
        <MT5LiveChartPanel isConnected={isConnected} onClose={() => setShowChartPanel(false)} />
      )}

      {/* Quick Start Guide - Only show when not connected */}
        {!isConnected && (
        <Card sx={{ 
          mt: 3, 
          borderRadius: 2, 
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
          mx: { xs: -0.5, sm: 0 },
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box sx={{
                width: 64,
                height: 64,
                bgcolor: 'orange.100',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}>
                <TrendingUpIcon sx={{ fontSize: 32, color: 'orange.600' }} />
              </Box>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Get Started with MT5 Trading
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Follow these simple steps to begin your trading journey
              </Typography>
            </Box>
            
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  bgcolor: 'orange.100',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5
                }}>
                  <Typography sx={{ color: 'orange.600', fontWeight: 600, fontSize: '1.125rem' }}>
                    1
                  </Typography>
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Connect to MT5
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Enter your MT5 server details and credentials to establish a secure connection
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  bgcolor: 'orange.100',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5
                }}>
                  <Typography sx={{ color: 'orange.600', fontWeight: 600, fontSize: '1.125rem' }}>
                    2
                  </Typography>
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Monitor Markets
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  View real-time prices and market movements for various trading instruments
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  bgcolor: 'orange.100',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5
                }}>
                  <Typography sx={{ color: 'orange.600', fontWeight: 600, fontSize: '1.125rem' }}>
                    3
                  </Typography>
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Execute Trades
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Place market or limit orders and monitor your open positions
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Features Section */}
      <Card sx={{ 
        mt: 3, 
        borderRadius: 2, 
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        mx: { xs: -0.5, sm: 0 },
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            MT5 Trading Features
          </Typography>
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 32,
                height: 32,
                bgcolor: 'success.100',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <TrendingUpIcon sx={{ fontSize: 16, color: 'success.600' }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Real-time Data
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.100',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <SpeedIcon sx={{ fontSize: 16, color: 'primary.600' }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Market Orders
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 32,
                height: 32,
                bgcolor: 'secondary.100',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AnalyticsIcon sx={{ fontSize: 16, color: 'secondary.600' }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Limit Orders
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 32,
                height: 32,
                bgcolor: 'orange.100',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <SecurityIcon sx={{ fontSize: 16, color: 'orange.600' }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Position Monitoring
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

        {/* Security Notice */}
      <Alert 
        severity="warning" 
        sx={{ 
          mt: 3, 
          borderRadius: 2,
          mx: { xs: -0.5, sm: 0 },
        }}
      >
        <AlertTitle sx={{ fontWeight: 600 }}>Important Security Notice</AlertTitle>
        <Box component="ul" sx={{ mb: 0, pl: 2 }}>
          <li>Always use demo accounts for testing before live trading</li>
          <li>Keep your MT5 credentials secure and never share them</li>
          <li>Monitor your positions regularly and use stop-loss orders</li>
          <li>Trading involves risk - only trade with funds you can afford to lose</li>
        </Box>
      </Alert>
    </Box>
  );
};

export default MT5Trading; 